import copy from 'clipboard-copy';
import Darkener from './Darkener';
import { DarkenerComponent } from './models/DarkenerComponent';
import style from './styles/App.module.scss';
import { FormattedMessage, useIntl } from 'react-intl';
import { useEffect, useRef } from 'react';

const ANIMATION_MILLISECONDS = 255;
const DELAY_MILLISECONDS = 5000;

export default function ShareSheet({ close, id, title }) {
    const darkener = useRef<DarkenerComponent>(null);
    const copyText = useRef<HTMLSpanElement>(null);
    const copyButton = useRef<HTMLButtonElement>(null);
    const intl = useIntl();
    const url = 'https://app.hallow.com/' + (intl.locale === 'en' ? '' : intl.locale + '/') + 'prayers/' + id;
    const embed = `<iframe class="hallowWidget" src="https://widget.hallow.com/${ intl.locale }/index.html"></iframe><style>iframe.hallowWidget {border:0;width:100%;height:100%;max-height:230px;min-height:130px;max-width:800px;min-width:280px;}</style>`;
    
    useEffect(() => {
        darkener.current.enable();
    }, []);
    
    const closeShare = () => {
        darkener.current.disable();
        close?.();
    };
    
    const closeByEsc = (e) => {
        if (['Esc', 'Escape'].includes(e.key)) {
            closeShare();
        }
    };
    
    const copyUrl = () => {
        copy(url).then(() => {
            copyText.current.classList.add(style.fade);
            copyButton.current.disabled = true;
        
            setTimeout(() => {
                copyText.current.innerText = intl.formatMessage({ id: 'general_word_copied', defaultMessage: 'Copied' });
                copyText.current.classList.remove(style.fade);
            }, ANIMATION_MILLISECONDS);
        
            setTimeout(() => {
                copyText.current.classList.add(style.fade);
                copyButton.current.disabled = false;
            
                setTimeout(() => {
                    copyText.current.innerText = intl.formatMessage({ id: 'general_word_copy', defaultMessage: 'Copy' });
                    copyText.current.classList.remove(style.fade);
                }, ANIMATION_MILLISECONDS);
            }, DELAY_MILLISECONDS);
        });
    };
    
    const copyCode = () => {
        return copy(embed);
    };
    
    return <div className={style.shareSheet} onKeyDown={closeByEsc}>
        <div className={style.buttonList}>
            <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${ encodeURIComponent(url) }&src=sdkpreparse%22%20class=%22fb-xfbml-parse-ignore`}
                target={'_blank'}
                rel={'noreferrer nofollow'}
                className={style.facebook}
                title={intl.formatMessage({ id: 'share_on_facebook', defaultMessage: 'Share on Facebook' })} />
            <a
                href={`https://wa.me/?text=${ encodeURIComponent(url) }`}
                target={'_blank'}
                rel={'noreferrer nofollow'}
                className={style.whatsApp}
                title={intl.formatMessage({ id: 'share_on_whatsapp', defaultMessage: 'Share via WhatsApp' })} />
            <a
                href={`https://twitter.com/intent/tweet?url=${ url }&text=${ title }&via=HallowApp`}
                target={'_blank'}
                rel={'noreferrer nofollow'}
                className={style.twitter}
                title={intl.formatMessage({ id: 'share_on_twitter', defaultMessage: 'Share on Twitter' })} />
            <a
                href={`mailto:?body=${ url }`}
                className={style.email}
                title={intl.formatMessage({ id: 'share_on_email', defaultMessage: 'Share via Email' })} />
            <button
                className={style.copyCode}
                onClick={copyCode}
                title={intl.formatMessage({ id: 'copy_embed_code', defaultMessage: 'Copy embed code to clipboard' })} />
        </div>
        <div className={style.urlCopy}>
            <input type={'text'} readOnly={true} value={url} autoFocus={true} />
            <button ref={copyButton} title={intl.formatMessage({ id: 'copy_url', defaultMessage: 'Copy URL to clipboard' })} onClick={copyUrl}>
                <span ref={copyText}>
                    <FormattedMessage id={'general_word_copy'} defaultMessage={'Copy'} />
                </span>
            </button>
        </div>
        <button className={style.exitShare} onClick={closeShare}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d={'M0 0L14 14M0 14L14 0'} stroke={'white'} />
            </svg>
        </button>
        <Darkener ref={darkener} darken={1} switchOff={closeShare} />
    </div>;
}